import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSocket } from "../utils/socket";
import NotificationLikeAddFriend from "./NotificationLikeAddFriend";
import { FaCoins, FaUser, FaCrown, FaUserShield } from "react-icons/fa";

const Navbar = ({ unreadCount, unreadNotificationCount, notifications, onToggleNotifications }) => {
  const { user: authUser, logout, fetchWithRetry } = useAuth();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationList, setNotificationList] = useState(notifications || []);
  const [justReceivedNotification, setJustReceivedNotification] = useState(false);
  const [coinBalance, setCoinBalance] = useState(authUser?.coinBalance || 0);
  const [membershipLevel, setMembershipLevel] = useState(authUser?.role || "user");
  const [membershipExpiration, setMembershipExpiration] = useState(authUser?.membership?.endDate || null);
  const [userMenuTimeout, setUserMenuTimeout] = useState(null);
  const [settingsMenuTimeout, setSettingsMenuTimeout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Định nghĩa fetchFriends với useCallback
  const fetchFriends = useCallback(async () => {
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/users/friends",
      });
      console.log("Làm mới danh sách bạn bè:", res.data.friends);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách bạn bè:", error.response || error);
    }
  }, [fetchWithRetry]);

  // Lắng nghe cập nhật thời gian thực từ WebSocket
  useEffect(() => {
    if (!authUser) return;

    const socket = getSocket();
    if (!socket.connected) {
      socket.connect();
    }

    const handleNotification = (notification) => {
      console.log("Nhận thông báo từ server:", notification);
      setNotificationList((prev) => {
        const isDuplicate = prev.some(
          (n) =>
            (n._id && n._id === notification._id) ||
            (!n._id && n.message === notification.message && n.createdAt === notification.createdAt)
        );
        if (!isDuplicate) {
          return [notification, ...prev];
        }
        return prev;
      });
      setIsNotificationOpen(true);
      setJustReceivedNotification(true);
    };

    const handleCoinBalanceUpdate = (data) => {
      if (data && data.coinBalance !== undefined) {
        setCoinBalance(data.coinBalance);
        console.log(`Cập nhật số dư coin: ${data.coinBalance}`);
      }
    };

    const handleMembershipUpdate = (data) => {
      if (data) {
        setMembershipLevel(data.membershipLevel || "user");
        setMembershipExpiration(data.membershipExpiration || null);
        console.log(`Cập nhật gói VIP: ${data.membershipLevel}, Hết hạn: ${data.membershipExpiration}`);
      }
    };

    const handleFriendListUpdate = () => {
      console.log("Nhận sự kiện cập nhật danh sách bạn bè");
      fetchFriends();
    };

    socket.on("notification", handleNotification);
    socket.on("coinBalanceUpdate", handleCoinBalanceUpdate);
    socket.on("membershipUpdate", handleMembershipUpdate);
    socket.on("friendListUpdate", handleFriendListUpdate);

    return () => {
      socket.off("notification", handleNotification);
      socket.off("coinBalanceUpdate", handleCoinBalanceUpdate);
      socket.off("membershipUpdate", handleMembershipUpdate);
      socket.off("friendListUpdate", handleFriendListUpdate);
    };
  }, [authUser, fetchFriends]);

  // Đồng bộ notificationList với notifications từ props
  useEffect(() => {
    if (notifications) {
      setNotificationList(notifications);
    }
  }, [notifications]);

  const handleLogout = () => {
    if (typeof logout === "function") {
      logout();
      navigate("/login");
    } else {
      console.error("logout is not a function");
      navigate("/login");
    }
  };

  const handleToggleNotification = () => {
    setIsNotificationOpen((prev) => {
      if (!prev && justReceivedNotification) {
        setJustReceivedNotification(false);
        return true;
      }
      return !prev;
    });
    if (onToggleNotifications) {
      onToggleNotifications();
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    if (onToggleNotifications) {
      onToggleNotifications(notificationId, "read");
    }
    setNotificationList((prev) =>
      prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
    );
  };

  const handleDelete = async (notificationId) => {
    if (onToggleNotifications) {
      onToggleNotifications(notificationId, "delete");
    }
    setNotificationList((prev) => prev.filter((n) => n._id !== notificationId));
  };

  const fetchNotifications = async () => {
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/users/notifications",
      });
      setNotificationList(res.data);
    } catch (error) {
      console.error("Lỗi khi lấy thông báo:", error.response || error);
    }
  };

  const handleAcceptFriendRequest = async (requestId) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }

      const notification = notificationList.find((n) => n.requestId === requestId);
      if (!notification) {
        throw new Error("Yêu cầu kết bạn không tồn tại!");
      }

      const response = await fetchWithRetry({
        method: "put",
        url: `/api/users/friend-request/${requestId}`,
        data: { action: "accept" },
      });

      alert(response.data.message || "Chấp nhận yêu cầu kết bạn thành công!");

      setNotificationList((prev) => prev.filter((n) => n.requestId !== requestId));
      await fetchFriends();
      await fetchNotifications();
    } catch (error) {
      console.error("Lỗi khi chấp nhận yêu cầu kết bạn:", error.response?.data || error);
      alert(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.status === 403
          ? "Bạn không có quyền xử lý yêu cầu này!"
          : error.response?.data?.message || error.message || "Lỗi không xác định"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserMenuEnter = () => {
    if (userMenuTimeout) clearTimeout(userMenuTimeout);
    setIsUserMenuOpen(true);
  };

  const handleUserMenuLeave = () => {
    const timeout = setTimeout(() => {
      setIsUserMenuOpen(false);
    }, 300);
    setUserMenuTimeout(timeout);
  };

  const handleSettingsMenuEnter = () => {
    if (settingsMenuTimeout) clearTimeout(settingsMenuTimeout);
    setIsSettingsMenuOpen(true);
  };

  const handleSettingsMenuLeave = () => {
    const timeout = setTimeout(() => {
      setIsSettingsMenuOpen(false);
    }, 300);
    setSettingsMenuTimeout(timeout);
  };

  const renderRoleIcon = () => {
    const role = authUser?.role || "user";
    switch (role) {
      case "admin":
      case "mod":
        return <FaUserShield className="text-red-500" title="Quản trị viên" />;
      case "vip_gold":
        return <FaCrown className="text-yellow-500" title="VIP Gold" />;
      case "vip_platinum":
        return <FaCrown className="text-gray-300" title="VIP Platinum" />;
      case "vip_diamond":
        return <FaCrown className="text-blue-500" title="VIP Diamond" />;
      default:
        return <FaUser className="text-green-100" title="Thành viên" />;
    }
  };

  const renderMembershipExpiration = () => {
    if (!membershipExpiration) return null;
    const expirationDate = new Date(membershipExpiration);
    const now = new Date();
    if (expirationDate < now) return <span className="text-red-500 text-xs ml-2">Hết hạn</span>;
    return (
      <span className="text-green-100 text-xs ml-2">
        Hết hạn: {expirationDate.toLocaleDateString("vi-VN")}
      </span>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-gradient-to-r from-green-600 to-green-800 shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            <Link
              to="/homepage"
              className="text-2xl font-bold text-white hover:text-green-200 transition duration-300"
            >
              Demo
            </Link>
            <div className="hidden md:flex flex-nowrap overflow-x-auto space-x-4 scrollbar-hide">
              <Link
                to="/homepage"
                className="text-green-100 hover:text-white px-2 py-2 text-sm font-medium rounded-md hover:bg-green-700 transition duration-300 border-b-2 border-transparent hover:border-green-300 whitespace-nowrap"
              >
                Trang chủ
              </Link>
              <Link
                to="/online-users"
                className="text-green-100 hover:text-white px-2 py-2 text-sm font-medium rounded-md hover:bg-green-700 transition duration-300 border-b-2 border-transparent hover:border-green-300 whitespace-nowrap"
              >
                Trực tuyến
              </Link>
              <Link
                to="/advanced-search"
                className="text-green-100 hover:text-white px-2 py-2 text-sm font-medium rounded-md hover:bg-green-700 transition duration-300 border-b-2 border-transparent hover:border-green-300 whitespace-nowrap"
              >
                Tìm kiếm
              </Link>
              <Link
                to="/chat-list"
                className="text-green-100 hover:text-white px-2 py-2 text-sm font-medium rounded-md hover:bg-green-700 transition duration-300 border-b-2 border-transparent hover:border-green-300 whitespace-nowrap"
              >
                Thư
              </Link>
              <Link
                to="/friends"
                className="text-green-100 hover:text-white px-2 py-2 text-sm font-medium rounded-md hover:bg-green-700 transition duration-300 border-b-2 border-transparent hover:border-green-300 whitespace-nowrap"
              >
                Bạn bè
              </Link>
              <Link
                to="/likes"
                className="text-green-100 hover:text-white px-2 py-2 text-sm font-medium rounded-md hover:bg-green-700 transition duration-300 border-b-2 border-transparent hover:border-green-300 whitespace-nowrap"
              >
                Likes
              </Link>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Link
              to="/upgrade"
              className="bg-green-500 text-white px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-green-600 transition duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap"
            >
              Nâng cấp Ngay!
            </Link>

            <div className="relative">
              <button
                onClick={handleToggleNotification}
                className="text-green-100 hover:text-white focus:outline-none transition duration-300 flex items-center space-x-1"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <span className="text-sm font-medium">Thông báo</span>
              </button>
              {notificationList.length > 0 && (
                <span className="absolute -top-1 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
                  {notificationList.filter((n) => !n.read).length + (unreadCount || 0)}
                </span>
              )}
              {isNotificationOpen && (
                <NotificationLikeAddFriend
                  notifications={notificationList}
                  onClose={() => setIsNotificationOpen(false)}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                  onAcceptFriendRequest={handleAcceptFriendRequest}
                />
              )}
            </div>

            {authUser && (
              <div
                className="relative flex items-center space-x-2"
                onMouseEnter={handleUserMenuEnter}
                onMouseLeave={handleUserMenuLeave}
              >
                <Link to="/profile">
                  <img
                    src={
                      authUser?.primaryPhoto?.photoUrl ||
                      authUser?.mainImage ||
                      authUser?.avatar ||
                      "https://placehold.co/50"
                    }
                    alt="User Avatar"
                    className="w-8 h-8 rounded-full object-cover border-2 border-green-300 shadow-md hover:scale-110 transition duration-300 cursor-pointer"
                    onError={(e) => (e.target.src = "https://placehold.co/32x32")}
                  />
                </Link>
                <div className="flex items-center space-x-1">
                  {renderRoleIcon()}
                  {membershipLevel !== "user" && renderMembershipExpiration()}
                </div>
                <button
                  onClick={() => navigate("/coin-purchase")}
                  className="flex items-center space-x-1 text-green-100 hover:text-white transition duration-300 group"
                >
                  <FaCoins className="w-5 h-5 text-yellow-300 group-hover:text-yellow-400 group-hover:scale-110 transition duration-300" />
                  <span className="text-sm font-medium">{coinBalance}</span>
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-12 w-56 bg-white rounded-lg shadow-xl py-2 border border-gray-100 animate-fade-in">
                    <Link
                      to="/view-profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Xem hồ sơ
                    </Link>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Chỉnh sửa hồ sơ
                    </Link>
                    <Link
                      to="/photos"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Ảnh
                    </Link>
                    <Link
                      to="/matches"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Phù hợp
                    </Link>
                    <Link
                      to="/interests"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Sở thích & Quan tâm
                    </Link>
                    <Link
                      to="/personality-questions"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Câu hỏi về tính cách
                    </Link>
                    <Link
                      to="/verify-profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Xác minh hồ sơ
                    </Link>
                    <Link
                      to="/hide-profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                    >
                      Ẩn Hồ Sơ
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-green-50 hover:text-red-700 transition duration-200"
                    >
                      Đăng xuất
                    </button>
                  </div>
                )}
              </div>
            )}

            <div
              className="relative"
              onMouseEnter={handleSettingsMenuEnter}
              onMouseLeave={handleSettingsMenuLeave}
            >
              <button className="text-green-100 hover:text-white focus:outline-none transition duration-300">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>
              {isSettingsMenuOpen && (
                <div className="absolute right-0 top-12 w-56 bg-white rounded-lg shadow-xl py-2 border border-gray-100 animate-fade-in">
                  <Link
                    to="/email-settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Địa Chỉ Email
                  </Link>
                  <Link
                    to="/change-password"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Mật khẩu
                  </Link>
                  <Link
                    to="/edit-profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Cài Đặt Hồ Sơ
                  </Link>
                  <Link
                    to="/invoices"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Lập Hóa Đơn
                  </Link>
                  <Link
                    to="/notifications"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Thông báo
                  </Link>
                  <Link
                    to="/language"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Lựa chọn ngôn ngữ
                  </Link>
                  <Link
                    to="/help"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Trợ giúp
                  </Link>
                  <Link
                    to="/upgrade-membership"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition duration-200"
                  >
                    Nâng cấp quyền hội viên
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-green-50 hover:text-red-700 transition duration-200"
                  >
                    Thoát
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;